import api from '@disco/api/server'
import {
  addToDisco,
  addToDiscoCancel,
  getSignedPublicPageUrl,
} from '@disco/state-management/actions/PublicPlaylistActions'
import { Theme } from '@radix-ui/themes'
import { Flex } from 'components/ds'
import * as React from 'react'
import { connect } from 'react-redux'
import Modal from 'react-modal'
import type { ClientLibraryId } from 'types/client-library'
import type { AddToDiscoObject, Business } from 'types/public-playlist'
import type { LoginDetails } from 'types/user'
import {
  darkThemeModalStyles,
  publicPlaylistDefaultModalStyles,
} from 'components/utils/modalStyles'
import AddToDiscoSuccessModal from '../add-to-disco-success-modal'
import AddToDiscoBusinessList from './business-list'
import AddToDiscoLoginForm from './login-form'
import type { AddToDiscoUser, State } from './types'

export type User = AddToDiscoUser
type Props = {
  currentBusinessId: number
  playlistId: number
  playlistVersionId: number
  shareId: number | null | undefined
  campaignId?: number | null | undefined
  recipientId?: number | null | undefined
  object: AddToDiscoObject
  businesses: Array<Business>
  success: boolean
  user: User
  onRequestClose: (...args: Array<any>) => any
  showModal: boolean
  href?: string
  clientLibraryId?: ClientLibraryId
  showModalSuccessDialog?: boolean
  displayPasswordRestoreLink?: boolean | string
  anonymousBusiness?: boolean
  mixpanelEventDetails?: Record<string, any>
  hideSuccessModal?: boolean
  addToDisco: typeof addToDisco
  addToDiscoCancel: typeof addToDiscoCancel
  colorTheme?: string
}

function mapStateToProps() {
  return {}
}

class AddToDiscoModal extends React.Component<Props, State> {
  state = {
    loading: false,
    status: 'INITIAL' as State['status'],
    requestedBusinessId: null,
    error: null,
    selectedBusiness: null,
    // Land on the sign-in step (not the gateway) if we're remounting after a
    // failed auth retry, so the error message is visible instead of silently
    // resetting to the initial "Save to DISCO" screen.
    showSignIn: Boolean(
      this.props.user.showLoginError ||
        this.props.user.error ||
        this.props.user.mfaCodeRequired,
    ),
  }

  _getUrl(): string {
    return this.props.href || getSignedPublicPageUrl()
  }

  _handleForgotPassword(email: string, returnUrl: string) {
    return api.sendPasswordRecoveryInstructions(email, returnUrl)
  }

  async _signupUrlAsync() {
    const host = 'https://disco.ac'
    const { object } = this.props
    let trackfile_ids

    switch (object.type) {
      case 'PLAYLIST':
        trackfile_ids = null
        break

      case 'SELECTION':
      case 'SECTION':
        trackfile_ids = object.ids
        break

      case 'TRACK':
        const all_files = object.track.all_files || {}
        trackfile_ids = Object.keys(all_files).map(f => ({
          track_file_id: all_files[f].id,
          format: f,
        }))
        break
    }

    let soUrlPart = ''

    try {
      if (trackfile_ids && this.props.playlistVersionId) {
        // api without csrf
        const res = await api.getCustomDownloadUrl(
          window.document.location.href, // TODO: remove call to window
          this.props.playlistVersionId,
          trackfile_ids,
        )
        soUrlPart = `so=${res.id}&`
      }
    } catch (err) {
      console.error(err)
    }

    return `${host}/signup?utm_source=save_to_disco_modal&${soUrlPart}su=${window.encodeURIComponent(
      this._getUrl(),
    )}`
  }

  async _addToDisco(
    business:
      | {
          url: string
          id: number
          name: string
        }
      | null
      | undefined,
    loginDetails?: LoginDetails,
  ) {
    const {
      object,
      clientLibraryId,
      showModalSuccessDialog,
      mixpanelEventDetails,
    } = this.props
    this.props.addToDisco(
      business
        ? {
            id: business.id,
            name: business.name,
            url: business.url,
          }
        : null,
      this._getUrl(),
      object,
      loginDetails,
      {
        shareId: this.props.shareId,
        playlistId: this.props.playlistId,
        campaignId: this.props.campaignId,
        recipientId: this.props.recipientId,
        clientLibraryId: clientLibraryId,
        showModalSuccessDialog,
      },
      mixpanelEventDetails,
    )
  }
  _getModalStyle = () => {
    const baseStyles =
      this.props.colorTheme === 'dark'
        ? darkThemeModalStyles
        : publicPlaylistDefaultModalStyles
    return {
      ...baseStyles,
      content: {
        ...baseStyles.content,
        borderRadius: '12px',
      },
    }
  }

  _handleLogin = (data: any) => {
    // for mfa auth use selected business as it's continuation of auth process
    const business = data.mfa_code ? this.state.selectedBusiness : null

    this._addToDisco(business, {
      username: data.username,
      password: data.password,
      captcha: data.captcha,
      mfa_code: data.mfa_code,
    })
  }

  _handleSaveBusiness = (business: Business) => {
    this.setState({
      selectedBusiness: business,
    })

    this._addToDisco(business)
  }

  _renderContents() {
    const { user, success, businesses } = this.props
    let requireLogin = false

    if (success && !this.props.hideSuccessModal) {
      return (
        <AddToDiscoSuccessModal
          onClose={this.props.addToDiscoCancel}
          modalStyle={this._getModalStyle()}
        />
      )
    }

    if (success && this.props.hideSuccessModal) {
      return null
    }

    if (user.error && user.error.providedLoginDetails) {
      switch (user.error.code) {
        case 'ADD_TO_DISCO_ERROR_UNAUTHORIZED':
        case 'ADD_TO_DISCO_ERROR_NO_DISCOS':
          requireLogin = true
      }
    }

    if (businesses.length === 0) requireLogin = true

    if (requireLogin || (!user.loggedIn && user.mfaCodeRequired)) {
      return (
        <Modal
          onRequestClose={this.props.onRequestClose}
          isOpen
          style={this._getModalStyle()}
        >
          <Theme asChild>
            {/* Override default Radix Theme styles */}
            <Flex minHeight="initial">
              <AddToDiscoLoginForm
                user={user}
                showSignIn={this.state.showSignIn}
                onShowSignIn={() => this.setState({ showSignIn: true })}
                onLoginUser={this._handleLogin}
                onForgotPassword={this._handleForgotPassword}
                onGetSignUpUrl={() => this._signupUrlAsync()}
              />
            </Flex>
          </Theme>
        </Modal>
      )
    }

    return (
      <Modal
        onRequestClose={this.props.onRequestClose}
        isOpen
        style={this._getModalStyle()}
      >
        <Theme>
          <AddToDiscoBusinessList
            onClose={this.props.onRequestClose}
            onSave={this._handleSaveBusiness}
            state={this.state}
            businesses={businesses}
            currentBusinessId={this.props.currentBusinessId}
          />
        </Theme>
      </Modal>
    )
  }

  render() {
    if (!this.props.showModal) return null
    return this._renderContents()
  }
}

export default connect(mapStateToProps, {
  addToDisco,
  addToDiscoCancel,
  // @ts-expect-error type connector
})(AddToDiscoModal) as any
